// react
import { Link } from 'react-router-dom';

// mui
import { Chip, Stack } from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { Icons } from '@/components/atoms/Icon/icons';

export interface FilterChipsItem {
  /** Stable identifier used to determine selection. */
  id: string;
  label: string;
  /** Optional icon shown before the label. */
  icon?: Icons;
  /** When provided, the chip renders as a Link and clicking navigates. */
  to?: string;
  /** Replace history entry when navigating via `to`. */
  replace?: boolean;
  /** Click handler for non-navigating chips. Ignored when `to` is set. */
  onClick?: () => void;
}

export interface FilterChipsProps {
  items: FilterChipsItem[];
  /** IDs that should render in the filled state. Empty means none. */
  activeIds?: string[];
}

/**
 * FilterChips renders a horizontal row of cherry-coloured filter chips.
 * Active items render filled, inactive items render outlined. An item with a
 * `to` prop navigates on click; otherwise `onClick` is fired. Used for the
 * Actions care-circle filter and the Library category filter.
 */
export const FilterChips = ({ items, activeIds = [] }: FilterChipsProps) => {
  const chipSx = {
    height: 36,
    fontSize: '1rem',
    borderRadius: 18,
    '& .MuiChip-label': { px: 1.5 },
  };

  return (
    <Stack
      direction='row'
      useFlexGap
      sx={{
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {items.map((item) => {
        const isActive = activeIds.includes(item.id);
        const chip = (
          <Chip
            label={item.label}
            color='cherry'
            clickable
            variant={isActive ? 'filled' : 'outlined'}
            icon={
              item.icon ? <Icon icon={item.icon} fontSize={14} /> : undefined
            }
            onClick={item.to ? undefined : item.onClick}
            sx={chipSx}
          />
        );
        return item.to ? (
          <Link
            key={item.id}
            to={item.to}
            replace={item.replace}
            style={{ textDecoration: 'none' }}
          >
            {chip}
          </Link>
        ) : (
          <span key={item.id}>{chip}</span>
        );
      })}
    </Stack>
  );
};

FilterChips.displayName = 'FilterChips';
export default FilterChips;
